/**
 * @feature Badge Service employee photos
 * @description Fetches the active Badge Service photo for an employee via the
 *   FINTECH PAPI route GET /v1/photos/by-login/{login}/active.
 *
 * Two modes (see the DaSFintechPAPIService badge_service_handler):
 *   - default            → { photo: { url } }  a pre-signed S3 URL (~1h expiry). Good for DISPLAY
 *                          (<img>/background-image is not CORS-gated).
 *   - ?return_blob=true  → { photo: { blob } } base64 image bytes returned IN the JSON. Required for
 *                          CAPTURE (html2canvas): the S3 bucket has no CORS, so reading its pixels
 *                          onto a canvas fails; base64-in-JSON comes through API Gateway (ACAO:*),
 *                          and a data: URL built from it is same-origin to the canvas.
 *
 * The endpoint is IAM-authorized (SigV4), so requests are signed with the app's Cognito Identity
 * Pool credentials — mirroring src/services/WorkDocsService.ts. Signing covers the query string.
 *
 * The photos endpoint is scoped to the app's own stage (alpha/beta/prod), resolved from
 * BackendGraphQLApiConfig's `fintech_papi`. Personal `dev` stacks normalize to `alpha`.
 */
import { fetchAuthSession } from 'aws-amplify/auth';
import { Signer } from '@aws-amplify/core/internals/utils';
import { logger } from 'src/logger';
import { FintechPapiPhoto, FintechPapiPhotoResponse } from 'src/api/interfaces';
import { BackendGraphQLApiConfig } from 'src/constants/ApiConfigConstants';
import { NORMALIZED_STAGE } from 'src/constants/EnvironmentConstants';

/**
 * Stage-scoped FINTECH PAPI base URL (from ApiConfigConstants.ts `fintech_papi`).
 * Falls back to the alpha endpoint if the stage is not found in config.
 */
const PHOTO_API_BASE =
  BackendGraphQLApiConfig.find((config) => config.environment === NORMALIZED_STAGE)?.fintech_papi ??
  'https://alpha.fintech-papi.device.finance.amazon.dev';

/** Short-lived cache (30s TTL) — avoids re-fetching the same login across a batch/remounts. */
const CACHE_TTL_MS = 30_000;
/** Shorter TTL for transient fetch errors so we don't retry-storm but still recover quickly. */
const ERROR_CACHE_TTL_MS = 5_000;
/** Keyed by `${login}:${mode}` so the display (url) and capture (data-url) variants cache separately. */
const photoCache = new Map<string, { value: string | null; expiry: number }>();

/** In-flight dedup — concurrent calls for the same cache key share one network request. */
const inflightRequests = new Map<string, Promise<string | null>>();

async function signedFetch(url: string, signal?: AbortSignal): Promise<Response> {
  const session = await fetchAuthSession();
  const credentials = session.credentials;
  if (!credentials) throw new Error('No AWS credentials available');

  const request = {
    method: 'GET',
    url,
    headers: { 'Content-Type': 'application/json' }
  };

  const signed = Signer.sign(
    request,
    {
      access_key: credentials.accessKeyId,
      secret_key: credentials.secretAccessKey,
      session_token: credentials.sessionToken || ''
    },
    { region: 'us-west-2', service: 'execute-api' }
  );

  return fetch(url, { method: 'GET', headers: signed.headers, signal });
}

/** Detect image MIME type from base64 magic bytes; the S3 object serves binary/octet-stream, so we can't trust a stored type. */
function mimeTypeFromBase64(base64: string): string {
  if (base64.startsWith('/9j/')) return 'image/jpeg';
  if (base64.startsWith('iVBOR')) return 'image/png';
  if (base64.startsWith('R0lGOD')) return 'image/gif';
  if (base64.startsWith('UklGR')) return 'image/webp';
  return 'image/jpeg';
}

/**
 * Fetches the active badge photo and maps it to a single string via `select`, with caching + dedup.
 * Returns null for no-photo (hidden/missing/HTTP error) or when `select` yields nothing.
 */
async function fetchPhoto(
  login: string,
  mode: 'url' | 'blob',
  select: (photo: FintechPapiPhoto) => string | null | undefined,
  signal?: AbortSignal
): Promise<string | null> {
  const key = login.toLowerCase();
  const cacheKey = `${key}:${mode}`;
  const cache = (value: string | null, ttl: number = CACHE_TTL_MS) => photoCache.set(cacheKey, { value, expiry: Date.now() + ttl });

  const cached = photoCache.get(cacheKey);
  if (cached && Date.now() < cached.expiry) return cached.value;

  // Only join/store the shared in-flight request when the caller passes no signal. A signal-bound
  // caller must run its own request: if it joined a shared one and later aborted, the abort would
  // reject the promise for every other joined consumer (spurious AbortError). See useProfilePhotoUrl.
  if (!signal) {
    const inflight = inflightRequests.get(cacheKey);
    if (inflight) return inflight;
  }

  const request = (async (): Promise<string | null> => {
    try {
      const query = mode === 'blob' ? '?return_blob=true&fullsize=true' : '';
      const url = `${PHOTO_API_BASE}/v1/photos/by-login/${encodeURIComponent(key)}/active${query}`;
      const response = await signedFetch(url, signal);

      if (!response.ok) {
        // 500 "No active photo for this person" is expected for people without a badge photo.
        logger.info('No badge photo available', { login: key, status: response.status });
        cache(null); // cache the "no photo" result so the batch flow doesn't re-request every employee
        return null;
      }

      const body = (await response.json()) as FintechPapiPhotoResponse;
      const photo = body.photo;
      if (!photo || photo.hidden) {
        cache(null);
        return null;
      }

      const value = select(photo) ?? null;
      cache(value);
      return value;
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') throw error;
      logger.warn('Badge photo fetch errored', { login: key });
      // Cache transient errors briefly so we don't retry-storm, but recover quickly.
      cache(null, ERROR_CACHE_TTL_MS);
      return null;
    } finally {
      if (!signal) inflightRequests.delete(cacheKey);
    }
  })();

  if (!signal) inflightRequests.set(cacheKey, request);
  return request;
}

/**
 * Returns the pre-signed S3 URL for an employee's active badge photo (for DISPLAY), or null when
 * there is no visible photo. Displaying this URL is not CORS-gated; reading its bytes in JS is —
 * use getActivePhotoDataUrl for that.
 *
 * @param login - Employee alias (e.g., "jsmith")
 * @param signal - Optional AbortSignal for request cancellation
 */
export async function getActivePhotoUrl(login: string, signal?: AbortSignal): Promise<string | null> {
  return fetchPhoto(login, 'url', (photo) => photo.url, signal);
}

/**
 * Returns the employee's active badge photo as a base64 `data:` URL (for CAPTURE / html2canvas), or
 * null when there is no visible photo. Requests `return_blob=true` so the bytes arrive in the JSON
 * (via API Gateway CORS) rather than from the CORS-less S3 bucket, making the result canvas-safe.
 *
 * @param login - Employee alias (e.g., "jsmith")
 * @param signal - Optional AbortSignal for request cancellation
 */
export async function getActivePhotoDataUrl(login: string, signal?: AbortSignal): Promise<string | null> {
  return fetchPhoto(login, 'blob', (photo) => (photo.blob ? `data:${mimeTypeFromBase64(photo.blob)};base64,${photo.blob}` : null), signal);
}
